import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react'
import { ArrowRight, ExternalLink, MessageSquare, X } from 'lucide-react'
import { ChatMarkdown } from './chat-markdown'
import {
  ChatHttpError,
  chatPriceLabel,
  connectChatSocket,
  fetchChatMessagesAfter,
  mergeMessages,
  searchChatPage,
  sendChatMessage,
  startOverviewChat,
  storeHref,
  type ChatMessage,
  type ChatProductCard,
  type SearchOverviewChat,
} from './storefront-chat-api'
import './storefront-chat.css'

// The store's chat widget (shopify-theme-v2 storefront-chat.tsx, USA-1225) opened from the AI
// overview's "Ask a follow-up" (USA-1257): the chat starts with the search and the overview's
// answer, so the first reply already knows them.

const TYPING_TIMEOUT_MS = 90_000
const MAX_MESSAGE_LENGTH = 2000

/** One "Ask a follow-up": the overview's opening and the question asked with it, if any. */
export type FollowUp = { chat: SearchOverviewChat; question: string | null; nonce: number }

type State = {
  status: 'starting' | 'ready' | 'unavailable'
  token: string | null
  messages: ChatMessage[]
  typing: boolean
  unread: number
  notice: 'rate-limited' | 'send-failed' | null
}

type Action =
  | { type: 'starting' }
  | { type: 'unavailable' }
  | { type: 'session'; token: string; messages: ChatMessage[] }
  | { type: 'message'; message: ChatMessage; open: boolean }
  | { type: 'messages'; messages: ChatMessage[] }
  | { type: 'typing'; active: boolean }
  | { type: 'notice'; value: State['notice'] }
  | { type: 'seen' }

const INITIAL: State = {
  status: 'starting',
  token: null,
  messages: [],
  typing: false,
  unread: 0,
  notice: null,
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'starting':
      return { ...state, status: 'starting' }
    case 'unavailable':
      return { ...state, status: 'unavailable', typing: false }
    case 'session':
      return { ...state, status: 'ready', token: action.token, messages: action.messages }
    case 'message': {
      const isNew = !state.messages.some((existing) => existing.id === action.message.id)
      const fromUs = action.message.author === 'team'
      return {
        ...state,
        messages: mergeMessages(state.messages, [action.message]),
        typing: fromUs ? false : state.typing,
        unread: !action.open && isNew && fromUs ? state.unread + 1 : state.unread,
        notice: action.message.author === 'shopper' ? null : state.notice,
      }
    }
    case 'messages':
      return { ...state, messages: mergeMessages(state.messages, action.messages) }
    case 'typing':
      return { ...state, typing: action.active }
    case 'notice':
      return { ...state, notice: action.value }
    case 'seen':
      return { ...state, unread: 0 }
  }
}

function ProductCard({ card }: { card: ChatProductCard }) {
  const price = chatPriceLabel(card.price)
  const href = storeHref(card.url) ?? undefined
  return (
    <li className="sf-chat__product" data-e2e="chat-product-card" data-product-handle={card.handle}>
      {/* eslint-disable-next-line no-restricted-syntax -- external storefront URL */}
      <a
        className="sf-chat__product-media"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={-1}
        aria-hidden="true"
      >
        {card.image_url ? <img src={card.image_url} alt="" loading="lazy" width={64} height={64} /> : null}
      </a>
      <div className="sf-chat__product-body">
        {/* eslint-disable-next-line no-restricted-syntax -- external storefront URL */}
        <a className="sf-chat__product-title" href={href} target="_blank" rel="noopener noreferrer">
          {card.title}
        </a>
        <div className="sf-chat__product-row">
          {price ? <span className="sf-chat__product-price">{price}</span> : null}
          {/* eslint-disable-next-line no-restricted-syntax -- external storefront URL */}
          <a className="sf-chat__product-view" href={href} target="_blank" rel="noopener noreferrer">
            View in store
            <ExternalLink size={12} strokeWidth={2} aria-hidden="true" />
          </a>
        </div>
      </div>
    </li>
  )
}

function MessageItem({ message }: { message: ChatMessage }) {
  const cards = message.product_cards ?? []
  const shopper = message.author === 'shopper'
  return (
    <li
      className={`sf-chat__message sf-chat__message--${shopper ? 'shopper' : 'team'}`}
      data-e2e="chat-message"
      data-author={message.author}
    >
      {message.body ? (
        <div className="sf-chat__bubble">
          {shopper ? <p>{message.body}</p> : <ChatMarkdown source={message.body} />}
        </div>
      ) : null}
      {cards.length ? (
        <ul className="sf-chat__products" aria-label="Products">
          {cards.map((card, index) => (
            <ProductCard key={`${card.handle}-${index}`} card={card} />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

export function StorefrontChat({
  followUp,
  open,
  onOpen,
  onClose,
}: {
  followUp: FollowUp
  open: boolean
  onOpen: () => void
  onClose: () => void
}) {
  const [state, dispatch] = useReducer(reducer, INITIAL)
  const [draft, setDraft] = useState('')
  const [page] = useState(() => searchChatPage(followUp.chat.query))
  const sessionRef = useRef<Promise<string | null> | null>(null)
  const socketRef = useRef<{ close: () => void } | null>(null)
  const askedRef = useRef<number | null>(null)
  const openRef = useRef(open)
  const lastIdRef = useRef<string | null>(null)
  const logRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const launcherRef = useRef<HTMLButtonElement>(null)
  const { status, typing } = state

  useLayoutEffect(() => {
    openRef.current = open
    lastIdRef.current = state.messages.at(-1)?.id ?? null
  })

  // One chat per overview: it opens with the search and the overview's answer.
  const ensureSession = useCallback((): Promise<string | null> => {
    if (sessionRef.current) return sessionRef.current
    dispatch({ type: 'starting' })
    sessionRef.current = (async () => {
      try {
        const session = await startOverviewChat(followUp.chat)
        dispatch({ type: 'session', token: session.token, messages: session.messages })
        socketRef.current?.close()
        socketRef.current = connectChatSocket(session.token, {
          message: (message) => dispatch({ type: 'message', message, open: openRef.current }),
          typing: (active) => dispatch({ type: 'typing', active }),
          reconnected: () => {
            const lastId = lastIdRef.current
            if (!lastId) return
            fetchChatMessagesAfter(session.token, lastId)
              .then((messages) => dispatch({ type: 'messages', messages }))
              .catch(() => undefined)
          },
        })
        return session.token
      } catch {
        sessionRef.current = null
        dispatch({ type: 'unavailable' })
        return null
      }
    })()
    return sessionRef.current
  }, [followUp.chat])

  useEffect(() => () => socketRef.current?.close(), [])

  const send = useCallback(
    async (body: string) => {
      const token = await ensureSession()
      if (!token) return false
      try {
        const message = await sendChatMessage(token, body, page)
        dispatch({ type: 'message', message, open: true })
        return true
      } catch (error) {
        const limited = error instanceof ChatHttpError && error.status === 429
        dispatch({ type: 'notice', value: limited ? 'rate-limited' : 'send-failed' })
        return false
      }
    },
    [ensureSession, page],
  )

  // Start the chat, and send the question typed into the overview with it.
  useEffect(() => {
    void ensureSession()
    if (askedRef.current === followUp.nonce) return
    askedRef.current = followUp.nonce
    const question = followUp.question
    if (question) {
      void send(question).then((sent) => {
        if (!sent) setDraft(question)
      })
    }
  }, [ensureSession, followUp.nonce, followUp.question, send])

  useEffect(() => {
    if (!open) return
    dispatch({ type: 'seen' })
    if (window.matchMedia?.('(pointer: fine)').matches) inputRef.current?.focus({ preventScroll: true })
  }, [open])

  // The reply should start within seconds; stop showing the dots if it never comes.
  useEffect(() => {
    if (!typing) return
    const timeout = window.setTimeout(() => dispatch({ type: 'typing', active: false }), TYPING_TIMEOUT_MS)
    return () => window.clearTimeout(timeout)
  }, [typing])

  // Keep the newest message in view.
  useLayoutEffect(() => {
    const log = logRef.current
    if (log) log.scrollTop = log.scrollHeight
  }, [state.messages.length, typing, open, status])

  const close = () => {
    onClose()
    window.requestAnimationFrame(() => launcherRef.current?.focus())
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault()
    const body = draft.trim()
    if (!body) return
    setDraft('')
    if (!(await send(body))) setDraft(body)
  }

  function onComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      void submit()
    }
  }

  return (
    <div className="sf-chat" data-e2e="storefront-chat" data-chat-open={open}>
      <button
        ref={launcherRef}
        type="button"
        className="sf-chat__launcher"
        hidden={open}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="sf-chat-panel"
        aria-label={state.unread ? `Chat with us, ${state.unread} new` : 'Chat with us'}
        onClick={onOpen}
      >
        <MessageSquare size={22} strokeWidth={1.8} aria-hidden="true" />
        <span className="sf-chat__launcher-label">Chat with us</span>
        {state.unread ? (
          <span className="sf-chat__badge" aria-hidden="true">
            {state.unread > 9 ? '9+' : state.unread}
          </span>
        ) : null}
      </button>

      <section
        id="sf-chat-panel"
        className="sf-chat__panel"
        role="dialog"
        aria-label="Chat with us"
        hidden={!open}
        data-e2e="chat-panel"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation()
            close()
          }
        }}
      >
        <header className="sf-chat__header">
          <span className="sf-chat__avatar" aria-hidden="true">
            <MessageSquare size={20} strokeWidth={1.8} />
          </span>
          <h2 className="sf-chat__title">Chat with us</h2>
          <button type="button" className="sf-chat__close" aria-label="Close chat" onClick={close}>
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        </header>

        <div className="sf-chat__log" ref={logRef}>
          {status === 'unavailable' ? (
            <div className="sf-chat__unavailable" role="alert">
              <p>Chat isn&apos;t available right now.</p>
              <button
                type="button"
                className="sf-button sf-button--secondary sf-button--small"
                onClick={() => void ensureSession()}
              >
                Try again
              </button>
            </div>
          ) : (
            <ol className="sf-chat__messages" role="log" aria-live="polite" aria-relevant="additions">
              {state.messages.map((message) => (
                <MessageItem key={message.id} message={message} />
              ))}
              {status === 'starting' || typing ? (
                <li className="sf-chat__message sf-chat__message--team">
                  <div className="sf-chat__typing" role="status" data-e2e="chat-typing">
                    <span className="sr-only">{status === 'starting' ? 'Loading chat' : 'Typing'}</span>
                    <i aria-hidden="true" />
                    <i aria-hidden="true" />
                    <i aria-hidden="true" />
                  </div>
                </li>
              ) : null}
            </ol>
          )}
        </div>

        <form className="sf-chat__composer" onSubmit={submit}>
          {state.notice ? (
            <p className="sf-chat__notice" role="alert">
              {state.notice === 'rate-limited'
                ? "You're sending messages quickly. Wait a moment, then try again."
                : "Your message didn't send. Try again."}
            </p>
          ) : null}
          <div className="sf-chat__input-row">
            <label className="sr-only" htmlFor="sf-chat-input">
              Message
            </label>
            <textarea
              id="sf-chat-input"
              ref={inputRef}
              rows={1}
              value={draft}
              maxLength={MAX_MESSAGE_LENGTH}
              placeholder="Ask us anything"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onComposerKeyDown}
              disabled={status === 'unavailable'}
            />
            <button
              type="submit"
              className="sf-chat__send"
              aria-label="Send"
              disabled={!draft.trim() || status === 'unavailable'}
            >
              <ArrowRight size={20} strokeWidth={2.2} aria-hidden="true" />
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}
